(() => {
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const themePreference = window.matchMedia('(prefers-color-scheme: dark)');
  const themeStorageKey = 'anatomedix-theme';
  const readStoredTheme = () => {
    try {
      const value = localStorage.getItem(themeStorageKey);
      return value === 'dark' || value === 'light' ? value : null;
    } catch (_) {
      return null;
    }
  };
  const preferredTheme = () => readStoredTheme() || (themePreference.matches ? 'dark' : 'light');
  const applyTheme = (theme, persist = false) => {
    const isDark = theme === 'dark';
    document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', isDark ? '#0b1317' : '#123d46');
    document.querySelectorAll('[data-theme-toggle]').forEach(button => {
      const label = isDark ? button.dataset.lightLabel : button.dataset.darkLabel;
      button.setAttribute('aria-pressed', String(isDark));
      if (label) {
        button.setAttribute('aria-label', label);
        button.setAttribute('title', label);
      }
    });
    if (persist) {
      try { localStorage.setItem(themeStorageKey, isDark ? 'dark' : 'light'); } catch (_) {}
    }
  };
  applyTheme(preferredTheme());
  themePreference.addEventListener('change', event => {
    if (!readStoredTheme()) applyTheme(event.matches ? 'dark' : 'light');
  });
  const animations = new Set();
  const header = document.querySelector('.site-header');
  const updateHeader = () => header?.classList.toggle('is-scrolled', window.scrollY > 8);
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });
  const motionAllowed = () => document.body.dataset.motion === 'true' && !motionPreference.matches && !window.Shopify?.designMode;
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      revealObserver.unobserve(entry.target);
      if (!motionAllowed() || !entry.target.animate) return;
      const cards = entry.target.classList.contains('product-card');
      const delay = cards ? Math.min(160, Array.from(entry.target.parentElement?.children || []).indexOf(entry.target) * 90) : 0;
      const animation = entry.target.animate([
        { opacity: 0, transform: 'translateY(20px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 620, delay, fill: 'both', easing: 'cubic-bezier(.2,.7,.3,1)' });
      animations.add(animation);
      animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
    });
  }, { threshold: 0.12 }) : null;
  motionPreference.addEventListener('change', () => {
    if (motionPreference.matches) animations.forEach(animation => animation.cancel());
  });
  function initialize(root = document) {
    root.querySelectorAll('[data-theme-toggle]').forEach(button => {
      if (button.dataset.themeReady) return;
      button.dataset.themeReady = 'true';
      button.addEventListener('click', () => {
        const nextTheme = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
        applyTheme(nextTheme, true);
      });
    });
    applyTheme(document.documentElement.dataset.theme || preferredTheme());
    if (revealObserver && motionAllowed()) {
      root.querySelectorAll('.hero-copy, .hero-art, .section-heading, .product-card, .story-grid>div, .about-grid>* , .about-values>div, .contact-grid>*, .model-questions, .models-hero-copy, .models-hero-visual, .models-knowledge-grid>*, .empty-state-models').forEach(element => {
        if (element.dataset.motionReady) return;
        element.dataset.motionReady = 'true';
        revealObserver.observe(element);
      });
    }
    root.querySelectorAll('[data-language-select]').forEach(select => {
      if (select.dataset.ready) return;
      select.dataset.ready = 'true';
      select.form.classList.add('language-enhanced');
      select.addEventListener('change', () => select.form.requestSubmit());
    });
    root.querySelectorAll('[data-product-section]').forEach(section => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const showMedia = id => {
        if (!id || !section.querySelector(`[data-slide="${CSS.escape(id)}"]`)) return;
        section.querySelectorAll('[data-slide]').forEach(slide => {
          slide.hidden = slide.dataset.slide !== id;
          if (slide.hidden) {
            slide.querySelectorAll('video').forEach(video => video.pause());
            slide.querySelectorAll('iframe').forEach(frame => { const src = frame.getAttribute('src'); if (src) frame.setAttribute('src', src); });
          }
        });
        section.querySelectorAll('[data-gallery-target]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.galleryTarget === id)));
      };
      section.querySelectorAll('[data-gallery-target]').forEach(button => button.addEventListener('click', () => showMedia(button.dataset.galleryTarget)));
      const slides = Array.from(section.querySelectorAll('[data-slide]'));
      section.querySelectorAll('[data-gallery-step]').forEach(button => {
        button.hidden = slides.length < 2;
        button.addEventListener('click', () => {
          if (slides.length < 2) return;
          const currentIndex = Math.max(0, slides.findIndex(slide => !slide.hidden));
          const nextIndex = (currentIndex + Number(button.dataset.galleryStep) + slides.length) % slides.length;
          showMedia(slides[nextIndex].dataset.slide);
        });
      });
      const select = section.querySelector('[data-variant-select]');
      if (!select) return;
      const updateVariant = (updateUrl = true) => {
        const option = select.selectedOptions[0];
        if (!option) return;
        const button = section.querySelector('[data-add-button]');
        const free = Number(option.dataset.rawPrice) === 0;
        section.querySelector('[data-price]').textContent = free ? button.dataset.inquireLabel : option.dataset.price;
        const compare = section.querySelector('[data-compare-price]');
        compare.textContent = option.dataset.compare;
        compare.hidden = !option.dataset.compare;
        button.disabled = option.dataset.purchasable !== 'true';
        section.querySelector('[data-add-text]').textContent = free ? button.dataset.inquireLabel : option.dataset.available === 'true' ? button.dataset.addLabel : button.dataset.soldLabel;
        const quantity = section.querySelector('[name="quantity"]');
        quantity.min = option.dataset.min || '1';
        quantity.step = option.dataset.increment || '1';
        if (option.dataset.max) quantity.max = option.dataset.max; else quantity.removeAttribute('max');
        quantity.value = quantity.min;
        showMedia(option.dataset.media);
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState({}, '', url);
        }
      };
      select.addEventListener('change', () => updateVariant());
      updateVariant(false);
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.mobile-menu[open], .nav-dropdown[open]').forEach(menu => { menu.open = false; menu.querySelector('summary').focus(); });
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.mobile-menu[open], .nav-dropdown[open]').forEach(menu => { if (!menu.contains(event.target)) menu.open = false; });
  });
})();
